import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChefHat } from "lucide-react";
import { toast } from "sonner";

export function OwnersTab({ owners, restaurants, reload }) {
  const [ownerFormOpen, setOwnerFormOpen] = useState(false);
  const [newOwner, setNewOwner] = useState({ name: "", email: "", password: "", phone: "", restaurant_id: "" });

  const createOwner = async () => {
    if (!newOwner.name || !newOwner.email || newOwner.password.length < 6 || !newOwner.restaurant_id) {
      toast.error("أكمل جميع الحقول (كلمة مرور 6 أحرف+)");
      return;
    }
    try {
      await api.post("/admin/restaurant-owners", newOwner);
      toast.success("تم إنشاء حساب صاحب المطعم");
      setOwnerFormOpen(false);
      setNewOwner({ name: "", email: "", password: "", phone: "", restaurant_id: "" });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    }
  };

  const removeOwner = async (o) => {
    if (!confirm(`إزالة صلاحيات صاحب المطعم ${o.name}؟`)) return;
    try {
      await api.delete(`/admin/restaurant-owners/${o.user_id}`);
      toast.success("تمت الإزالة");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل");
    }
  };

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-[#6B6B6B]">إدارة حسابات أصحاب المطاعم</p>
        <Button
          onClick={() => setOwnerFormOpen(true)}
          className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white"
          data-testid="admin-add-owner"
        >
          <Plus className="w-4 h-4 me-1" />
          إضافة صاحب مطعم
        </Button>
      </div>
      <div className="bg-white rounded-2xl border border-[#EAEAEA] overflow-hidden">
        {owners.length === 0 ? (
          <div className="p-10 text-center text-[#6B6B6B]">لا يوجد أصحاب مطاعم بعد</div>
        ) : (
          <ul className="divide-y divide-[#F0F0F0]">
            {owners.map((o) => (
              <li key={o.user_id} className="p-4 flex items-center gap-3" data-testid={`owner-${o.user_id}`}>
                <div className="w-10 h-10 rounded-xl bg-[#FFF1EA] flex items-center justify-center flex-shrink-0">
                  <ChefHat className="w-5 h-5 text-[#E05D3A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#1A1A1A] truncate">{o.name}</p>
                  <p className="text-xs text-[#6B6B6B] truncate">
                    {o.email} · {o.restaurant_name_ar || o.owned_restaurant_id}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeOwner(o)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`remove-owner-${o.user_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

    <Dialog open={ownerFormOpen} onOpenChange={setOwnerFormOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>إضافة حساب صاحب مطعم</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>المطعم</Label>
            <Select value={newOwner.restaurant_id} onValueChange={(v) => setNewOwner({ ...newOwner, restaurant_id: v })}>
              <SelectTrigger data-testid="owner-form-restaurant"><SelectValue placeholder="اختر مطعم" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {restaurants.map((r) => (
                  <SelectItem key={r.id} value={r.id}>{r.name_ar}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>اسم المالك</Label><Input value={newOwner.name} onChange={(e) => setNewOwner({ ...newOwner, name: e.target.value })} data-testid="owner-form-name" /></div>
          <div><Label>البريد</Label><Input type="email" value={newOwner.email} onChange={(e) => setNewOwner({ ...newOwner, email: e.target.value })} data-testid="owner-form-email" /></div>
          <div><Label>كلمة المرور (6+ أحرف)</Label><Input type="password" value={newOwner.password} onChange={(e) => setNewOwner({ ...newOwner, password: e.target.value })} data-testid="owner-form-password" /></div>
          <div><Label>رقم الجوال</Label><Input value={newOwner.phone} onChange={(e) => setNewOwner({ ...newOwner, phone: e.target.value })} placeholder="+9665xxxxxxxx" data-testid="owner-form-phone" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOwnerFormOpen(false)}>إلغاء</Button>
          <Button onClick={createOwner} className="bg-[#E05D3A] hover:bg-[#C24B2C] text-white" data-testid="owner-form-save">إضافة</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
